import { buildAttributionParams } from './attribution';

type DataLayerEvent = Record<string, unknown>;

type AnalyticsWindow = Window & {
  dataLayer?: DataLayerEvent[];
};

const isBrowser = typeof window !== 'undefined';

const getAnalyticsWindow = (): AnalyticsWindow | null => {
  if (!isBrowser) {
    return null;
  }

  const w = window as AnalyticsWindow;

  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }

  return w;
};

const pushToDataLayer = (event: DataLayerEvent): void => {
  const w = getAnalyticsWindow();

  if (!w || !Array.isArray(w.dataLayer)) {
    return;
  }

  w.dataLayer.push(event);
};

export const setAnalyticsUserId = (userId: string | null): void => {
  pushToDataLayer({
    event: 'set_user_id',
    user_id: userId,
  });
};

/** Only a non-empty string is ever pushed, so GTM never reads null/'' as an ID. */
const withUserId = (userId?: string | null): { user_id?: string } =>
  typeof userId === 'string' && userId ? { user_id: userId } : {};

export type SignUpMethod = 'email' | 'google' | 'facebook' | 'apple';

type TrackSignUpParams = {
  email: string;
  method: SignUpMethod;
  userId?: string | null;
};

export const trackSignUp = ({
  email,
  method,
  userId,
}: TrackSignUpParams): void => {
  pushToDataLayer({
    event: 'sign_up',
    user_data: {
      email,
    },
    method,
    ...withUserId(userId),
    ...buildAttributionParams(),
  });
};

type TrackStartTrialParams = {
  email: string;
  planName: string;
  userId?: string | null;
};

export const trackStartTrial = ({
  email,
  planName,
  userId,
}: TrackStartTrialParams): void => {
  pushToDataLayer({
    event: 'start_trial',
    user_data: {
      email,
    },
    plan_name: planName,
    ...withUserId(userId),
    ...buildAttributionParams(),
  });
};

type BillingInterval = 'yearly' | 'monthly';

type TrackViewPricingParams = {
  billingInterval: BillingInterval;
};

const viewedPricingIntervals = new Set<BillingInterval>();

type TrackPurchaseParams = {
  email: string;
  transactionId: string;
  value: number;
  currency: string;
  planName: string;
  billingInterval: BillingInterval;
  memberCount?: number;
  userId?: string | null;
};

export const trackPurchase = ({
  email,
  transactionId,
  value,
  currency,
  planName,
  billingInterval,
  memberCount,
  userId,
}: TrackPurchaseParams): void => {
  pushToDataLayer({
    event: 'purchase',
    user_data: {
      email,
    },
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
    },
    plan_name: planName,
    billing_interval: billingInterval,
    ...(typeof memberCount === 'number' ? { member_count: memberCount } : {}),
    ...withUserId(userId),
    ...buildAttributionParams(),
  });
};

type TrackSubscriptionRenewParams = {
  email: string;
  transactionId: string;
  value: number;
  currency: string;
  planName: string;
  billingInterval: BillingInterval;
};

export const trackSubscriptionRenew = ({
  email,
  transactionId,
  value,
  currency,
  planName,
  billingInterval,
}: TrackSubscriptionRenewParams): void => {
  pushToDataLayer({
    event: 'subscription_renew',
    user_data: {
      email,
    },
    plan_name: planName,
    billing_interval: billingInterval,
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
    },
  });
};

type TrackSubscriptionCancelParams = {
  planName: string;
  billingInterval: BillingInterval;
};

export const trackSubscriptionCancel = ({
  planName,
  billingInterval,
}: TrackSubscriptionCancelParams): void => {
  pushToDataLayer({
    event: 'subscription_cancel',
    plan_name: planName,
    billing_interval: billingInterval,
  });
};

export const trackViewPricing = ({
  billingInterval,
}: TrackViewPricingParams): void => {
  if (viewedPricingIntervals.has(billingInterval)) return;

  viewedPricingIntervals.add(billingInterval);
  pushToDataLayer({
    event: 'view_pricing',
    billing_interval: billingInterval,
  });
};
